import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, BarChart3, Wallet, ArrowUpRight, ArrowDownRight,
  ChevronRight, RefreshCw, Eye, EyeOff, Briefcase, CandlestickChart, Coins,
  Rocket, FileCheck, Layers, DollarSign, Activity, Receipt,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrencySpaced, maskCurrency } from '@/lib/market';
import { rebaseAllocationExcludingFD, buildAllocation } from '@/lib/netWorthAllocation';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DONUT_COLORS = ['#ffd87a', '#e8a838', '#c97a3a', '#dae4c4', '#c8d9b0', '#a86830', '#eae9da', '#d9d9d9'];

const ASSET_LABEL_MAP = {
  stocks: 'Equities', mf: 'Mutual Funds', fd: 'Fixed Deposits',
  bonds: 'Bonds', etf: 'ETFs', fo: 'Futures & Options',
  nps: 'National Pension System', pms: 'Portfolio Mgmt. Services',
  aif: 'Alternative Inv. Funds', ppf: 'PPF / EPF',
  gold: 'Gold / Silver', realestate: 'Real Estate',
};

const formatPercent = (v) => {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
};

// ── Donut Chart ───────────────────────────────────────────────────────────────
const DonutChart = ({ data, size = 200 }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  let angle = -90;
  const r = size / 2 - 14, cx = size / 2, cy = size / 2, ir = r * 0.52;
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="cursor-pointer" onMouseLeave={() => setHovered(null)} onMouseMove={handleMouseMove}>
        {data.map((item, i) => {
          const pct = item.value / total, deg = pct * 360;
          const s = angle, e2 = angle + deg; angle = e2;
          const sr = s * Math.PI / 180, er = e2 * Math.PI / 180;
          const d = [
            `M ${cx + r * Math.cos(sr)} ${cy + r * Math.sin(sr)}`,
            `A ${r} ${r} 0 ${deg > 180 ? 1 : 0} 1 ${cx + r * Math.cos(er)} ${cy + r * Math.sin(er)}`,
            `L ${cx + ir * Math.cos(er)} ${cy + ir * Math.sin(er)}`,
            `A ${ir} ${ir} 0 ${deg > 180 ? 1 : 0} 0 ${cx + ir * Math.cos(sr)} ${cy + ir * Math.sin(sr)}`,
            'Z',
          ].join(' ');
          return <path key={i} d={d} fill={item.color} stroke="none"
            style={{ opacity: hovered == null || hovered === i ? 1 : 0.35, transition: 'opacity 0.15s' }}
            onMouseEnter={() => setHovered(i)} />;
        })}
      </svg>
      {hovered != null && data[hovered] && (
        <div className="absolute z-50 pointer-events-none bg-white border border-[#e5e5e5] text-xs px-2.5 py-1.5 whitespace-nowrap rounded-lg shadow-md"
          style={{ left: tooltipPos.x + 10, top: tooltipPos.y - 38, transform: tooltipPos.x > size * 0.6 ? 'translateX(-100%)' : 'none' }}>
          <span className="font-medium text-[#c97a3a]">{data[hovered].name}</span>
          <span className="ml-2">{(data[hovered].percentage ?? data[hovered].value).toFixed(1)}%</span>
        </div>
      )}
    </div>
  );
};

// ── Market Indices Strip ──────────────────────────────────────────────────────
function IndicesStrip({ indices }) {
  if (!indices.length) return null;
  return (
    <div className="flex items-center gap-6 px-4 py-2 bg-white border border-[#e5e5e5] rounded-xl mb-4 overflow-x-auto">
      {indices.map((idx, i) => (
        <div key={i} className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-[#4a4a4a]">{idx.name}</span>
          <span className="text-xs font-bold text-[#1a1a1a]">{idx.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <span className={`flex items-center text-xs font-medium ${idx.change_percent >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
            {idx.change_percent >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(idx.change_percent).toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function ConsolidatedDashboard() {
  const { user } = useAuth();
  const { dealStageMap } = useAppData();
  const navigate = useNavigate();
  const market = user?.market || 'IN';
  const [loading, setLoading] = useState(true);
  const [showValues, setShowValues] = useState(true);

  const fmt = (v) => showValues ? formatCurrencySpaced(v, market) : maskCurrency(market);

  // Data
  const [publicMarkets,    setPublicMarkets]    = useState(null);
  const [pmData,           setPmData]           = useState({});
  const [altData,          setAltData]          = useState({});
  const [realEstate,       setRealEstate]       = useState({ totalValue: 0, totalAnnualRent: 0 });
  const [deals,            setDeals]            = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [indices,          setIndices]          = useState([]);
  const [assetAllocation,  setAssetAllocation]  = useState([]);
  const [totals,           setTotals]           = useState({ netWorth: 0, pmAUM: 0, cashAltAUM: 0, othersAUM: 0 });
  const [performanceYTD,   setPerformanceYTD]   = useState(null);
  const [performance3Y,    setPerformance3Y]    = useState(null);
  const [benchmarks,       setBenchmarks]       = useState([]);

  const PM_CATS  = ['vc', 'pe', 'search', 'long_only', 'credit', 'infra', 'hedge_fund', 'secondaries', 'special_situations', 'pre_ipo', 'mezzanine', 'fof', 'co_investment', 'royalty', 'natural_resources'];
  const ALT_CATS = ['gold', 'art', 'cash', 'alternatives', 'ip'];

  const fetchAll = useCallback(async () => {
    const [
      consolidationRes, reRes, approvalsRes, indicesRes,
      pmResults, altResults, dealsRes, fdRes, perfYTDRes, perf3YRes, benchmarksRes,
    ] = await Promise.all([
      axios.get(`${API}/analytics/consolidation`).catch(() => ({ data: null })),
      axios.get(`${API}/real-estate/portfolio`).catch(() => ({ data: null })),
      axios.get(`${API}/deals/ic-memos`).catch(() => ({ data: [] })),
      axios.get(`${API}/markets/market/indices`).catch(() => ({ data: [] })),
      Promise.all(PM_CATS.map(c => axios.get(`${API}/private-markets/${c}`).then(r => [c, r.data || []]).catch(() => [c, []]))),
      Promise.all(ALT_CATS.map(c => axios.get(`${API}/other-assets/${c}`).then(r => [c, r.data || []]).catch(() => [c, []]))),
      axios.get(`${API}/deals/`).catch(() => ({ data: [] })),
      axios.get(`${API}/markets/holdings?asset_class=fd`).catch(() => ({ data: [] })),
      axios.get(`${API}/analytics/performance?period=ytd`).catch(() => ({ data: null })),
      axios.get(`${API}/analytics/performance?period=3y`).catch(() => ({ data: null })),
      axios.get(`${API}/analytics/performance/all-benchmarks?period=ytd`).catch(() => ({ data: [] })),
    ]);

    const pmObj  = Object.fromEntries(pmResults);
    const altObj = Object.fromEntries(altResults);
    setPmData(pmObj);
    setAltData(altObj);
    setPerformanceYTD(perfYTDRes.data);
    setPerformance3Y(perf3YRes.data);
    setBenchmarks(Array.isArray(benchmarksRes.data) ? benchmarksRes.data : []);
    setDeals(dealsRes.data || []);
    setIndices(indicesRes.data || []);

    const allMemos = approvalsRes.data || [];
    setPendingApprovals(allMemos.filter(m => ['submitted', 'under_review'].includes(m.status)));

    // Public markets: strip FD out (FD is counted under Cash & Alternatives)
    const fdHoldings = fdRes.data || [];
    const fdValue    = fdHoldings.reduce((s, d) => s + (d.current_value || d.avg_price || 0), 0);
    const fdInvested = fdHoldings.reduce((s, d) => s + (d.invested_value || d.avg_price || 0), 0);
    const rawPub = consolidationRes.data || { total_value: 0, total_invested: 0, total_gain: 0, gain_percent: 0, holdings_count: 0, xirr: null, asset_allocation: {} };
    const investedXFD = (rawPub.total_invested || 0) - fdInvested;
    const gainXFD     = (rawPub.total_gain || 0) - (fdValue - fdInvested);
    const pub = {
      ...rawPub,
      total_value:      (rawPub.total_value || 0) - fdValue,
      total_invested:   investedXFD,
      total_gain:       gainXFD,
      gain_percent:     investedXFD > 0 ? (gainXFD / investedXFD) * 100 : 0,
      holdings_count:   Math.max(0, (rawPub.holdings_count || 0) - fdHoldings.length),
      asset_allocation: rebaseAllocationExcludingFD(rawPub.asset_allocation),
    };
    setPublicMarkets(pub);

    const reData = reRes.data || {};
    setRealEstate({
      totalValue: reData.total_value || 0,
      totalAnnualRent: reData.total_annual_rent || 0,
      portfolioYield: reData.portfolio_yield || 0,
    });

    const sumOf = (rows, fn) => (rows || []).reduce((t, d) => t + (fn(d) || 0), 0);
    const pmAUM      = sumOf(Object.values(pmObj).flat(), d => d.current_value || d.nav);
    const cashAltAUM = fdValue + sumOf(altObj.cash, d => d.balance) + sumOf(altObj.alternatives, d => d.nav);
    // Others = real estate + gold + art + IP (cash & alternatives have their own bucket)
    const othersAUM  = (reData.total_value || 0)
      + sumOf(altObj.gold, d => (d.units || 0) * (d.current_price || 0))
      + sumOf(altObj.art, d => d.current_value)
      + sumOf(altObj.ip, d => d.current_value);
    const netWorth   = (pub.total_value || 0) + pmAUM + cashAltAUM + othersAUM;
    setTotals({ netWorth, pmAUM, cashAltAUM, othersAUM });

    const alloc = buildAllocation({
      publicValue: pub.total_value || 0,
      publicAssetAllocation: pub.asset_allocation,
      pmAUM, cashAltAUM, othersAUM, total: netWorth,
    });
    setAssetAllocation(alloc);

    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-[#1a1a1a] animate-spin mx-auto mb-3" />
          <p className="text-[#4a4a4a] text-sm">Loading your wealth overview…</p>
        </div>
      </div>
    );
  }

  const publicGainPct   = publicMarkets?.gain_percent  || 0;
  const publicGain      = publicMarkets?.total_gain    || 0;
  const publicInvested  = publicMarkets?.total_invested || 0;
  const publicValue     = publicMarkets?.total_value    || 0;
  const dayChange       = publicMarkets?.day_change     || 0;
  const xirr            = publicMarkets?.xirr;

  const closedDeals  = deals.filter(d => d.stage === 'closed');
  const activeDeals  = deals.filter(d => d.stage !== 'closed' && d.stage !== 'passed');
  const cashV        = (altData.cash || []).reduce((s, d) => s + (d.balance || 0), 0);
  const avgInterest  = altData.cash?.length ? (altData.cash.reduce((s, c) => s + (c.interest_rate || 0), 0) / altData.cash.length) : 0;
  const annualInterest = cashV * avgInterest / 100;
  const annualRent     = realEstate.totalAnnualRent || 0;

  // FMV = total portfolio fair market value across all asset classes
  const goldV  = (altData.gold  || []).reduce((s, d) => s + (d.units || 0) * (d.current_price || 0), 0);
  const artV   = (altData.art   || []).reduce((s, d) => s + (d.current_value || 0), 0);
  const altV   = (altData.alternatives || []).reduce((s, d) => s + (d.nav || 0), 0);
  const ipV    = (altData.ip    || []).reduce((s, d) => s + (d.current_value || 0), 0);
  const reVal  = realEstate.totalValue || 0;
  const totalFMV = totals.netWorth;

  // Avg IRR across all private market holdings
  const irrEntries = Object.values(pmData).flat().filter(d => d.irr != null);
  const avgIRRValue = irrEntries.length ? irrEntries.reduce((t, d) => t + d.irr, 0) / irrEntries.length : null;

  // FMV gain: public gain + private gain where cost basis is available
  const pmAllFlat = Object.values(pmData).flat();
  const pmInvestedAll = pmAllFlat.reduce((s, d) => s + (d.invested || 0), 0);
  const pmGainAll = pmAllFlat.reduce((s, d) => s + ((d.current_value || d.nav || 0) - (d.invested || 0)), 0);
  const totalInvestedKnown = publicInvested + pmInvestedAll;
  const totalGainKnown = publicGain + (pmInvestedAll > 0 ? pmGainAll : 0);
  const fmvGainPct = totalInvestedKnown > 0 ? (totalGainKnown / totalInvestedKnown * 100) : null;

  // Liquidity: liquid = public + cash + gold (mark-to-market, sellable)
  //            illiquid = private markets + real estate
  //            semi = art + alternatives + IP (not counted in either)
  const liquidValue = publicValue + cashV + goldV;
  const liquidPct   = totalFMV > 0 ? Math.round(liquidValue / totalFMV * 100) : 0;
  const illiquidPct = 100 - liquidPct;

  const PM_OTHERS_CATS = ['hedge_fund', 'secondaries', 'special_situations', 'pre_ipo', 'mezzanine', 'fof', 'co_investment', 'royalty', 'natural_resources'];

  const firstName = user?.name
    ? (() => { const p = user.name.split(' '); return p[0].length <= 2 && p.length > 1 ? p[0] + ' ' + p[1] : p[0]; })()
    : null;

  return (
    <div style={{ padding: '28px 36px 52px', background: '#f8f6f1', minHeight: '100vh' }} data-testid="consolidated-dashboard">
      {/* Topbar */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 18 }}>
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 6 }}>
            Family Office Overview{user?.name ? ` · ${user.name}` : ''}
          </div>
          <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1, color: '#2a2a2a', margin: 0 }}>
            {firstName ? `Welcome back, ${firstName}!` : 'Portfolio Overview'}
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
          <IndicesStrip indices={indices} />
          <span style={{ fontSize: 11.5, color: '#a8a8a8', whiteSpace: 'nowrap' }}>
            As of {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
          <button
            onClick={() => setShowValues(v => !v)}
            style={{ width: 34, height: 34, borderRadius: 9, border: '1px solid #e5e5e5', background: '#fff', display: 'grid', placeItems: 'center', color: '#666', cursor: 'pointer' }}
          >
            {showValues ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* Hero Card */}
      <section style={{
        display: 'grid', gridTemplateColumns: '1.1fr auto 1.2fr', gap: 0,
        background: '#ffffff', border: '0.6px solid #ffd879', borderRadius: 20,
        boxShadow: '0 2px 18px rgba(0,0,0,0.07)', marginBottom: 22, overflow: 'hidden',
      }}>
        {/* Left — Net Worth */}
        <div style={{ padding: '36px 40px 32px', display: 'flex', flexDirection: 'column', gap: 0, background: 'linear-gradient(96deg, #fcfcf5 0%, #fef3c2 100%)' }}>
          <div style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 10 }}>Total Net Worth</div>
          <div style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, color: '#000', fontSize: 62, letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 18px' }}>
            {fmt(totals.netWorth)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {fmvGainPct != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 99, background: 'rgba(16,185,129,0.12)', color: '#059669', fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(16,185,129,0.30)' }}>
                <TrendingUp size={14} /> {formatPercent(fmvGainPct)} overall
              </span>
            )}
            {xirr != null && (
              <span style={{ fontSize: 13, color: '#666' }}>
                XIRR <strong style={{ color: '#000' }}>{formatPercent(xirr)}</strong>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 0, borderTop: '1px solid #e5e5e5', marginTop: 28 }}>
            {[
              { label: 'Public Markets',  value: fmt(publicValue) },
              { label: 'Private Markets', value: fmt(totals.pmAUM) },
              { label: 'Cash & Alts',     value: fmt(totals.cashAltAUM) },
              { label: 'Others',          value: fmt(totals.othersAUM) },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '18px 0 0', ...(i > 0 ? { borderLeft: '1px solid #e5e5e5', paddingLeft: 22 } : {}) }}>
                <span style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: '#a8a8a8', fontWeight: 600 }}>{s.label}</span>
                <span style={{ fontSize: 19, fontWeight: 600, color: '#000', fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, background: 'linear-gradient(to bottom, transparent 10%, #e5e5e5 30%, #e5e5e5 70%, transparent 90%)', alignSelf: 'stretch' }} />

        {/* Right — Allocation */}
        <div style={{ padding: '32px 36px 28px', display: 'flex', flexDirection: 'column', gap: 18, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8' }}>Allocation</span>
            <span style={{ fontSize: 12, color: '#a8a8a8' }}>{assetAllocation.length} asset classes</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1 }}>
            {assetAllocation.length > 0 && <DonutChart data={assetAllocation} size={186} />}
            <ul style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
              {assetAllocation.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: i < assetAllocation.length - 1 ? '1px solid #e5e5e5' : 'none', fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, color: '#404040', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  <span style={{ fontWeight: 600, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' }}>{item.percentage.toFixed(1)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Stat Band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 13, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 22, overflow: 'hidden' }}>
        {[
          { k: 'Public Markets',    v: fmt(publicValue),      delta: formatPercent(publicGainPct), tone: publicGainPct >= 0 ? '#059669' : '#e53535' },
          { k: 'Unrealised P&L',   v: fmt(publicGain),       delta: `on ${fmt(publicInvested)}`, tone: publicGain >= 0 ? '#059669' : '#e53535' },
          { k: 'Private Markets',   v: fmt(totals.pmAUM),     delta: `${closedDeals.length} invested`, tone: '#666' },
          { k: 'Portfolio FMV',     v: fmt(totalFMV),         delta: fmvGainPct != null ? `${formatPercent(fmvGainPct)} gain` : 'all classes', tone: fmvGainPct != null ? (fmvGainPct >= 0 ? '#059669' : '#e53535') : '#666' },
          { k: 'Liquidity',         v: `${liquidPct}%`,       delta: `${illiquidPct}% locked`, tone: '#666' },
          { k: 'Pending Approvals', v: String(pendingApprovals.length), delta: 'awaiting review', tone: '#1a1a1a', flag: true },
        ].map((cell, i) => (
          <div key={i} style={{ flex: 1, minWidth: 120, padding: '18px 20px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', background: cell.flag ? 'linear-gradient(96deg, #ffe7a8 0%, #ffd879 100%)' : undefined, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: cell.flag ? 'rgba(26,26,26,0.65)' : '#a8a8a8' }}>{cell.k}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', margin: '8px 0 5px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
            <div style={{ fontSize: 11, fontWeight: 500, color: cell.flag ? '#1a1a1a' : cell.tone }}>{cell.delta}</div>
          </div>
        ))}
      </div>

      {/* Performance Band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 13, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 18, overflow: 'hidden' }}>
        {(() => {
          // True YTD (Modified Dietz vs Dec-31 closes); null when price history is unavailable
          const ytd = performanceYTD?.period_return ?? null;
          const t3y = performance3Y?.annualized_return ?? null;
          const cells = [
            { k: 'YTD Return', v: ytd != null ? formatPercent(ytd) : '—', tone: ytd != null ? (ytd >= 0 ? '#059669' : '#e53535') : '#1a1a1a', sub: 'Public markets', title: performanceYTD?.methodology },
            { k: '3-Year Return', v: t3y != null ? formatPercent(t3y) : '—', tone: t3y != null ? (t3y >= 0 ? '#059669' : '#e53535') : '#1a1a1a', sub: 'Annualised' },
            { k: 'Avg. IRR', v: avgIRRValue != null ? `${avgIRRValue.toFixed(1)}%` : '—', tone: avgIRRValue != null ? (avgIRRValue >= 0 ? '#059669' : '#e53535') : '#1a1a1a', sub: 'Private markets' },
            ...benchmarks.map(bm => ({ k: bm.benchmark_name, v: bm.benchmark_return != null ? `${bm.benchmark_return.toFixed(1)}%` : '—', tone: '#1a1a1a', sub: 'YTD benchmark' })),
          ];
          return cells.map((cell, i) => (
            <div key={cell.k} title={cell.title || undefined} style={{ flex: 1, minWidth: 120, padding: '16px 20px', textAlign: 'center', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none' }}>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: cell.tone, margin: '8px 0 5px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
              <div style={{ fontSize: 11, fontWeight: 500, color: '#a8a8a8' }}>{cell.sub}</div>
            </div>
          ));
        })()}
      </div>

      <div className="space-y-5">

        {/* ── Overview Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Public Markets breakdown */}
          <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/markets')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                  <CandlestickChart className="w-3.5 h-3.5 text-[#1a1a1a]" />
                </div>
                Public Markets
              </CardTitle>
              <CardDescription>{publicMarkets?.holdings_count || 0} holdings · {fmt(publicValue)}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              {/* Key metrics row */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Invested',  value: fmt(publicInvested) },
                  { label: 'FMV',       value: fmt(publicValue) },
                  { label: 'Avg. XIRR', value: xirr != null ? formatPercent(xirr) : '—', color: xirr != null ? (xirr >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]') : undefined },
                ].map((m, i) => (
                  <div key={i} className="p-2 bg-[#f9f9f9] rounded-lg text-center">
                    <p className="text-[9px] text-[#4a4a4a] uppercase tracking-wide">{m.label}</p>
                    <p className={`text-xs font-bold mt-0.5 ${m.color || 'text-[#1a1a1a]'} truncate`}>{m.value}</p>
                  </div>
                ))}
              </div>
              {/* Asset allocation donut */}
              {publicMarkets?.asset_allocation && (() => {
                const entries = Object.entries(publicMarkets.asset_allocation).filter(([, p]) => p > 0).sort(([, a], [, b]) => b - a).slice(0, 6);
                const chartData = entries.map(([asset, p], i) => ({ name: ASSET_LABEL_MAP[asset] || asset, value: p, color: DONUT_COLORS[i] }));
                return (
                  <div className="flex flex-col items-center gap-3">
                    <DonutChart data={chartData} size={130} />
                    <div className="space-y-1 w-full">
                      {chartData.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                          <span className="text-xs text-[#4a4a4a] flex-1 truncate">{item.name}</span>
                          <span className="text-xs font-semibold text-[#1a1a1a]">{item.value.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <Link to="/markets" className="mt-auto flex items-center justify-between p-2.5 rounded-lg border border-[#e5e5e5] hover:border-[#fed879] hover:shadow-sm transition-all">
                <span className="text-xs font-medium text-[#1a1a1a]">View Public Markets</span>
                <ChevronRight className="w-3.5 h-3.5 text-[#4a4a4a]" />
              </Link>
            </CardContent>
          </Card>

          {/* Private Markets breakdown */}
          <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/private-markets')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                  <Rocket className="w-3.5 h-3.5 text-[#1a1a1a]" />
                </div>
                Private Markets
              </CardTitle>
              <CardDescription>{activeDeals.length} active deals · {closedDeals.length} closed</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              {(() => {
                const pmOthersVal = PM_OTHERS_CATS.reduce((s, k) => s + (pmData[k] || []).reduce((ss, d) => ss + (d.current_value || d.nav || 0), 0), 0);
                const bars = [
                  { name: 'Startups',      value: closedDeals.reduce((s, d) => s + (d.actual_investment_amount || d.target_investment_amount || 0), 0) },
                  { name: 'PE / VC',       value: [...(pmData.vc||[]),...(pmData.pe||[])].reduce((s,d)=>s+(d.current_value||0),0) },
                  { name: 'Search Funds',  value: (pmData.search||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                  { name: 'Long-Only Eq.', value: (pmData.long_only||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                  { name: 'Priv. Credit',  value: (pmData.credit||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                  { name: 'Infrastructure',value: (pmData.infra||[]).reduce((s,d)=>s+(d.current_value||0),0) },
                  { name: 'Others',        value: pmOthersVal },
                ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: DONUT_COLORS[i] }));
                const allPM = Object.values(pmData).flat();
                const irrEntries  = allPM.filter(d => d.irr  != null);
                const avgIRR  = irrEntries.length  ? (irrEntries.reduce((s,d)=>s+d.irr,0)/irrEntries.length).toFixed(1) : null;
                const pmInvestedVal = allPM.reduce((s, d) => s + (d.invested || 0), 0);
                return (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Invested',  value: pmInvestedVal > 0 ? fmt(pmInvestedVal) : '—' },
                        { label: 'FMV',       value: fmt(totals.pmAUM) },
                        { label: 'Avg. XIRR', value: avgIRR ? `${avgIRR}%` : '—', color: avgIRR ? (parseFloat(avgIRR) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]') : undefined },
                      ].map((m, i) => (
                        <div key={i} className="p-2 bg-[#f9f9f9] rounded-lg text-center">
                          <p className="text-[9px] text-[#4a4a4a] uppercase tracking-wide">{m.label}</p>
                          <p className={`text-xs font-bold mt-0.5 truncate ${m.color || 'text-[#1a1a1a]'}`}>{m.value}</p>
                        </div>
                      ))}
                    </div>
                    {bars.length > 0 && (
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={bars} layout="vertical" margin={{ left: 0, right: 10, top: 2, bottom: 2 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 9, fill: '#4a4a4a' }} tickFormatter={v => fmt(v).split(' ')[0]} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#1a1a1a' }} width={76} />
                          <Tooltip formatter={v => [fmt(v), 'AUM']} contentStyle={{ fontSize: 11, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8 }} />
                          <Bar dataKey="value" barSize={12} radius={[0, 3, 3, 0]}>
                            {bars.map((_, i) => <Cell key={i} fill={bars[i].color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </>
                );
              })()}
              <Link to="/private-markets" className="mt-auto flex items-center justify-between p-2.5 rounded-lg border border-[#e5e5e5] hover:border-[#fed879] hover:shadow-sm transition-all">
                <span className="text-xs font-medium text-[#1a1a1a]">View Private Markets</span>
                <ChevronRight className="w-3.5 h-3.5 text-[#4a4a4a]" />
              </Link>
            </CardContent>
          </Card>

          {/* Alternatives + Income */}
          <Card className="bg-white border-[#e5e5e5] shadow-sm flex flex-col cursor-pointer hover:border-[#ffd87a] hover:shadow-md transition-all" onClick={() => navigate('/others')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#1a1a1a] flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#dae4c4] flex items-center justify-center">
                  <Layers className="w-3.5 h-3.5 text-[#1a1a1a]" />
                </div>
                Alternatives & Income
              </CardTitle>
              <CardDescription>Others · Annual income sources</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 gap-4">
              {(() => {
                const goldV  = (altData.gold||[]).reduce((s,d)=>s+(d.units||0)*(d.current_price||0),0);
                const artV   = (altData.art||[]).reduce((s,d)=>s+(d.current_value||0),0);
                const altV   = (altData.alternatives||[]).reduce((s,d)=>s+(d.nav||0),0);
                const ipV    = (altData.ip||[]).reduce((s,d)=>s+(d.current_value||0),0);
                const reVal  = realEstate.totalValue || 0;
                const altBars = [
                  { label: 'Real Estate',   value: reVal },
                  { label: 'Gold',          value: goldV },
                  { label: 'Cash',          value: cashV },
                  { label: 'Alternatives',  value: altV  },
                  { label: 'Art & IP',      value: artV + ipV },
                ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: DONUT_COLORS[i] }));
                return (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Alt AUM',     value: fmt(totals.cashAltAUM + totals.othersAUM - reVal) },
                        { label: 'Real Estate', value: fmt(reVal) },
                        { label: 'Annual Rent', value: fmt(annualRent) },
                        { label: 'Interest Inc.',value: fmt(annualInterest) },
                      ].map((m, i) => (
                        <div key={i} className="p-2 bg-[#f9f9f9] rounded-lg text-center">
                          <p className="text-[9px] text-[#4a4a4a] uppercase tracking-wide">{m.label}</p>
                          <p className="text-xs font-bold text-[#1a1a1a] mt-0.5 truncate">{m.value}</p>
                        </div>
                      ))}
                    </div>
                    {altBars.length > 0 && (
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={altBars} layout="vertical" margin={{ left: 0, right: 10, top: 2, bottom: 2 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 9, fill: '#4a4a4a' }} tickFormatter={v => fmt(v).split(' ')[0]} />
                          <YAxis type="category" dataKey="label" tick={{ fontSize: 9, fill: '#1a1a1a' }} width={80} />
                          <Tooltip formatter={v => [fmt(v), 'Value']} contentStyle={{ fontSize: 11, background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8 }} />
                          <Bar dataKey="value" barSize={12} radius={[0, 3, 3, 0]}>
                            {altBars.map((_, i) => <Cell key={i} fill={altBars[i].color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </>
                );
              })()}
              <Link to="/others" className="mt-auto flex items-center justify-between p-2.5 rounded-lg border border-[#e5e5e5] hover:border-[#fed879] hover:shadow-sm transition-all">
                <span className="text-xs font-medium text-[#1a1a1a]">View Others</span>
                <ChevronRight className="w-3.5 h-3.5 text-[#4a4a4a]" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
